import { Card } from '@/presentation/components/ui/card'
import { Badge } from '@/presentation/components/ui/badge'
import { Button } from '@/presentation/components/ui/button'
import { DriverAvatar } from '@/presentation/components/ui/driver-avatar'
import { Driver } from '@/core/domain/entities/Driver'
import { formatCurrency } from '@/lib/utils'
import { MessageCircle, Lock, Eye } from 'lucide-react'
import DriverStatsColumnSkeleton from '../Skeletons/DriverStatsColumnSkeleton'
import { useNavigateWithParams } from '@/presentation/hooks/useNavigateWithParams'

type DriverStatistics = {
  driverId: string
  totalRides: number
  completedRides: number
  comissionEarnings: number
}

type DriversCardsProps = {
  filteredDrivers: Driver[]
  driverStatisticsMap: Map<string, DriverStatistics>
  isLoadingStats?: boolean
}

export default function DriversCards({
  filteredDrivers,
  driverStatisticsMap,
  isLoadingStats = false,
}: DriversCardsProps) {
  const { navigateWithParams } = useNavigateWithParams()

  const handleDriverClick = (driverId: string) => {
    navigateWithParams(`/driver/${driverId}`)
  }

  return (
    <div className="grid gap-4 p-4">
      {filteredDrivers.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-muted-foreground text-sm">
            Nenhum motorista encontrado
          </div>
        </div>
      ) : (
        filteredDrivers.map((currentDriver) => {
          const driverStatistics = driverStatisticsMap.get(currentDriver.id)
          const isRestricted = currentDriver.acceptsCash === 2
          return (
            <Card
              key={currentDriver.id}
              className={`p-4 ${isRestricted ? 'bg-pinkGarupa/10 border-pinkGarupa' : ''}`}
            >
              <div className="space-y-3">
                {/* Driver info */}
                <div className="flex flex-col items-center gap-2">
                  <div className="relative">
                    <DriverAvatar
                      photo={currentDriver.photo}
                      name={currentDriver.fullName}
                      size="md"
                    />
                    {isRestricted && (
                      <div className="absolute -top-2 -right-2 bg-pinkGarupa rounded-full p-1">
                        <Lock className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <h3 className="font-medium text-sm">
                      {currentDriver.fullName ?? 'Sem nome'}
                    </h3>
                    {isRestricted && (
                      <span className="text-pinkGarupa font-semibold text-xs">
                        Motorista Restrito
                      </span>
                    )}
                  </div>
                </div>

                {/* Status and Subscription */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground text-center">
                      Status
                    </span>
                    <Badge
                      variant={currentDriver.isOnline ? 'default' : 'secondary'}
                      className="text-xs w-full justify-center"
                    >
                      {currentDriver.isOnline ? 'Online' : 'Offline'}
                    </Badge>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground text-center">
                      Assinatura
                    </span>
                    <Badge
                      variant={
                        currentDriver.hasSubscription ? 'default' : 'secondary'
                      }
                      className="text-xs w-full justify-center"
                    >
                      {currentDriver.hasSubscription ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </div>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <div className="text-lg font-bold">
                      {isLoadingStats ? (
                        <DriverStatsColumnSkeleton />
                      ) : (
                        (driverStatistics?.totalRides ?? 0)
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Corridas
                    </div>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <div className="text-lg font-bold text-green-600">
                      {isLoadingStats ? (
                        <DriverStatsColumnSkeleton />
                      ) : (
                        formatCurrency(driverStatistics?.comissionEarnings ?? 0)
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Comissão
                    </div>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <div className="text-lg font-bold flex items-center justify-center gap-1">
                      {(currentDriver.rating ?? 0).toFixed(1)}
                      <span className="text-yellow-500 text-sm">⭐</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Avaliação
                    </div>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <div className="text-sm font-medium text-muted-foreground">
                      {currentDriver.lastRideAt ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Última Corrida
                    </div>
                  </div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded">
                  <div className="text-lg font-bold">
                    {currentDriver.classification}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Classificação
                  </div>
                </div>

                {/* Driver Whatsapp Button */}
                <div className="text-center">
                  {currentDriver.phone ? (
                    <Button
                      asChild
                      variant="default"
                      size="sm"
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      <a
                        href={`https://wa.me/55${currentDriver.phone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Chamar no WhatsApp
                      </a>
                    </Button>
                  ) : (
                    <Button
                      disabled
                      variant="secondary"
                      size="sm"
                      className="w-full"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Número não disponível
                    </Button>
                  )}
                </div>

                {/* Actions - Driver detail route */}
                <div className="pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => handleDriverClick(currentDriver.id)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Ver Detalhes
                  </Button>
                </div>
              </div>
            </Card>
          )
        })
      )}
    </div>
  )
}
